import React, { useState, useRef } from 'react'
import {
  Container,
  Typography,
  Paper,
  TextField,
  Button,
  Box,
  Card,
  CardContent,
  Grid,
  Chip,
  Alert,
  LinearProgress,
  Divider,
  Switch,
  FormControlLabel,
  useMediaQuery,
  useTheme,
  CircularProgress
} from '@mui/material'
import {
  Edit as EditIcon,
  Send as SendIcon,
  Assessment as AssessmentIcon,
  Star as StarIcon,
  Speed as StreamIcon
} from '@mui/icons-material'
import { motion } from 'framer-motion'
import { apiClient, API_ENDPOINTS, buildApiUrl } from '../config/api'
import toast from 'react-hot-toast'
import ReactMarkdown from 'react-markdown'


const writingPrompts = [
  {
    type: 'IELTS Task 1',
    title: 'Describe a Chart',
    prompt: 'The chart below shows the percentage of households in owned and rented accommodation in England and Wales between 1918 and 2011. Summarize the information by selecting and reporting the main features, and make comparisons where relevant.'
  },
  {
    type: 'IELTS Task 2',
    title: 'Opinion Essay',
    prompt: 'Some people think that universities should provide graduates with the knowledge and skills needed in the workplace. Others think that the true function of a university should be to give access to knowledge for its own sake, regardless of whether the course is useful to an employer. What, in your opinion, should be the main function of a university?'
  },
  {
    type: 'General',
    title: 'Describe Your Hometown',
    prompt: 'Write about your hometown. Describe what makes it special, the people, the culture, and your favorite places there.'
  },
  {
    type: 'General',
    title: 'Future Plans',
    prompt: 'Write about your plans for the future. What do you want to achieve in the next 5 years?'
  }
]

function Writing() {
  const [selectedPrompt, setSelectedPrompt] = useState(null)
  const [essay, setEssay] = useState('')
  const [feedback, setFeedback] = useState(null)
  const [isLoading, setIsLoading] = useState(false)
  const [studentLevel, setStudentLevel] = useState('B1')
  const [streamingEnabled, setStreamingEnabled] = useState(true)
  const [isStreaming, setIsStreaming] = useState(false)
  const [streamingFeedback, setStreamingFeedback] = useState('')
  const eventSourceRef = useRef(null)
  const theme = useTheme()
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'))

  const handleSubmitEssay = async () => {
    if (!essay.trim()) {
      toast.error('Vui lòng viết bài luận của bạn')
      return
    }

    if (streamingEnabled) {
      await handleStreamingWriting()
    } else {
      await handleRegularWriting()
    }
  }

  const handleRegularWriting = async () => {
    setIsLoading(true)
    try {
      const response = await apiClient.post(API_ENDPOINTS.TEACHER.WRITING, {
        text: essay,
        type: selectedPrompt?.type || 'General',
        studentLevel: studentLevel
      })
      
      setFeedback(response.data)
      toast.success('Đã nhận được phản hồi từ AI!')
    } catch (error) {
      console.error('Writing assessment error:', error)
      toast.error('Có lỗi xảy ra khi đánh giá bài viết')
    } finally {
      setIsLoading(false)
    }
  }

  const handleStreamingWriting = async () => {
    setIsStreaming(true)
    setStreamingFeedback('')
    setFeedback(null)
    
    try {
      const response = await fetch(buildApiUrl(API_ENDPOINTS.TEACHER.WRITING_STREAM), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          text: essay,
          type: selectedPrompt?.type || 'General',
          studentLevel: studentLevel
        })
      })

      if (!response.ok) {
        throw new Error('Network response was not ok')
      }

      const reader = response.body.getReader()
      const decoder = new TextDecoder()
      let buffer = ''
      let fullContent = ''

      while (true) {
        const { done, value } = await reader.read()
        if (done) break

        buffer += decoder.decode(value, { stream: true })
        const lines = buffer.split('\n')
        buffer = lines.pop() || ''

        for (const line of lines) {
          if (line.startsWith('data: ')) {
            const data = line.slice(6)
            
            if (data === '[DONE]') continue
            
            try {
              const parsed = JSON.parse(data)
              
              if (parsed.type === 'chunk' && parsed.content) {
                fullContent += parsed.content
                setStreamingFeedback(fullContent)
              } else if (parsed.type === 'complete') {
                const completeFeedback = {
                  original: essay,
                  feedback: parsed.fullContent || fullContent,
                  detailed_feedback: parsed.fullContent || fullContent,
                  timestamp: new Date().toISOString()
                }
                setFeedback(completeFeedback)
                toast.success('Đã nhận được phản hồi từ AI!')
              } else if (parsed.type === 'error') {
                throw new Error(parsed.error)
              }
            } catch (parseError) {
              console.error('Parse error:', parseError)
            }
          }
        }
      }
    } catch (error) {
      console.error('Streaming writing error:', error)
      toast.error('Có lỗi xảy ra khi đánh giá bài viết')
    } finally {
      setIsStreaming(false)
      setStreamingFeedback('')
    }
  }

  const handlePromptSelect = (prompt) => {
    setSelectedPrompt(prompt)
    setEssay('')
    setFeedback(null)
  }

  const wordCount = essay.trim().split(/\s+/).filter(word => word.length > 0).length

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      <motion.div
        initial={{ opacity: 0, y: 30 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.8 }}
      >
        {/* Header */}
        <Box sx={{ textAlign: 'center', mb: 4 }}>
          <EditIcon sx={{ fontSize: 60, color: 'primary.main', mb: 2 }} />
          <Typography variant="h3" component="h1" sx={{ mb: 2, fontWeight: 600 }}>
            Luyện viết
          </Typography>
          <Typography variant="h6" color="text.secondary" sx={{ maxWidth: 600, mx: 'auto' }}>
            Cải thiện kỹ năng viết tiếng Anh với sự hỗ trợ của AI
          </Typography>
        </Box>

        <Grid container spacing={4}>
          {/* Writing Prompts */}
          <Grid item xs={12} md={4}>
            <Paper elevation={3} sx={{ p: 3, height: 'fit-content' }}>
              <Typography variant="h6" sx={{ mb: 2, display: 'flex', alignItems: 'center' }}>
                <AssessmentIcon sx={{ mr: 1 }} />
                Chọn đề bài
              </Typography>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                {writingPrompts.map((prompt, index) => (
                  <Card 
                    key={index}
                    sx={{ 
                      cursor: 'pointer',
                      border: selectedPrompt?.title === prompt.title ? 2 : 1,
                      borderColor: selectedPrompt?.title === prompt.title ? 'primary.main' : 'divider',
                      '&:hover': { borderColor: 'primary.main' }
                    }}
                    onClick={() => handlePromptSelect(prompt)}
                  >
                    <CardContent sx={{ p: 2 }}>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                        <Chip label={prompt.type} size="small" color="primary" />
                      </Box>
                      <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1 }}>
                        {prompt.title}
                      </Typography>
                      <Typography variant="body2" color="text.secondary" sx={{ 
                        display: '-webkit-box',
                        WebkitLineClamp: 3,
                        WebkitBoxOrient: 'vertical',
                        overflow: 'hidden'
                      }}>
                        {prompt.prompt}
                      </Typography>
                    </CardContent>
                  </Card>
                ))}
              </Box>
            </Paper>
          </Grid>

          {/* Writing Area */}
          <Grid item xs={12} md={8}>
            <Paper elevation={3} sx={{ p: 3 }}>
              {selectedPrompt ? (
                <>
                  <Box sx={{ mb: 3 }}>
                    <Typography variant="h6" sx={{ mb: 1 }}>
                      {selectedPrompt.title}
                    </Typography>
                    <Typography variant="body1" color="text.secondary" sx={{ mb: 2 }}>
                      {selectedPrompt.prompt}
                    </Typography>
                    <Divider />
                  </Box>

                  <TextField
                    fullWidth
                    multiline
                    rows={15}
                    placeholder="Viết bài luận của bạn ở đây..."
                    value={essay}
                    onChange={(e) => setEssay(e.target.value)}
                    sx={{ mb: 2 }}
                  />

                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2, flexWrap: 'wrap', gap: 1 }}>
                    <Typography variant="body2" color="text.secondary">
                      Số từ: {wordCount}
                    </Typography>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                      <FormControlLabel
                        control={
                          <Switch
                            checked={streamingEnabled}
                            onChange={(e) => setStreamingEnabled(e.target.checked)}
                            size={isMobile ? 'small' : 'medium'}
                          />
                        }
                        label={
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                            <StreamIcon fontSize="small" />
                            <Typography variant="body2">Streaming</Typography>
                          </Box>
                        }
                      />
                      <Button
                        variant="contained"
                        onClick={handleSubmitEssay}
                        disabled={isLoading || isStreaming || !essay.trim()}
                        startIcon={isStreaming ? <CircularProgress size={16} /> : <SendIcon />}
                      >
                        {isStreaming ? 'Đang phân tích...' : isLoading ? 'Đang đánh giá...' : 'Gửi để đánh giá'}
                      </Button>
                    </Box>
                  </Box>

                  {isLoading && <LinearProgress sx={{ mb: 2 }} />}

                  {/* Streaming Feedback */}
                  {isStreaming && streamingFeedback && (
                    <motion.div
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ duration: 0.3 }}
                    >
                      <Paper elevation={1} sx={{ p: 3, bgcolor: 'grey.50', mb: 2 }}>
                        <Typography variant="h6" sx={{ mb: 2, display: 'flex', alignItems: 'center' }}>
                          <CircularProgress size={20} sx={{ mr: 1 }} />
                          Đang phân tích bài viết...
                        </Typography>
                        <Box sx={{ 
                          '& p': { mb: 1 },
                          '& ul, & ol': { pl: 2, mb: 1 },
                          '& li': { mb: 0.5 },
                          '& h1, & h2, & h3, & h4, & h5, & h6': { mt: 2, mb: 1 },
                          '& blockquote': { borderLeft: '4px solid #ccc', pl: 2, fontStyle: 'italic' },
                          '& code': { bgcolor: 'grey.100', px: 0.5, py: 0.25, borderRadius: 0.5 },
                          '& pre': { bgcolor: 'grey.100', p: 1, borderRadius: 1, overflow: 'auto' }
                        }}>
                          <ReactMarkdown>{streamingFeedback}</ReactMarkdown>
                        </Box>
                      </Paper>
                    </motion.div>
                  )}

                  {feedback && (
                    <motion.div
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ duration: 0.5 }}
                    >
                      <Alert severity="success" sx={{ mb: 2 }}>
                        Đánh giá hoàn tất! Xem phản hồi chi tiết bên dưới.
                      </Alert>
                      <Paper elevation={1} sx={{ p: 3, bgcolor: 'grey.50' }}>
                        <Typography variant="h6" sx={{ mb: 2, display: 'flex', alignItems: 'center' }}>
                          <StarIcon sx={{ mr: 1, color: 'warning.main' }} />
                          Phản hồi từ AI
                        </Typography>
                        
                        {/* Overall Band Score */}
                        {feedback.overall_band && (
                          <Box sx={{ mb: 3 }}>
                            <Typography variant="h6" sx={{ mb: 1 }}>
                              Điểm tổng thể: <Chip label={`Band ${feedback.overall_band}`} color="primary" size="large" />
                            </Typography>
                          </Box>
                        )}
                        
                        {/* Essay Info */}
                        <Box sx={{ mb: 3, display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                          {feedback.essayType && (
                            <Chip label={`Loại bài: ${feedback.essayType}`} variant="outlined" />
                          )}
                          {feedback.studentLevel && (
                            <Chip label={`Trình độ: ${feedback.studentLevel}`} variant="outlined" />
                          )}
                          {feedback.wordCount && (
                            <Chip label={`Số từ: ${feedback.wordCount}`} variant="outlined" />
                          )}
                        </Box>
                        
                        {/* Detailed Feedback */}
                        {feedback.detailed_feedback && (
                          <Box sx={{ mb: 2 }}>
                            <Typography variant="h6" sx={{ mb: 2 }}>
                              Phản hồi chi tiết:
                            </Typography>
                            {(() => {
                              try {
                                // Extract JSON from markdown code block if present
                                let jsonString = feedback.detailed_feedback
                                if (jsonString.includes('```json')) {
                                  const startIndex = jsonString.indexOf('```json') + 7
                                  const endIndex = jsonString.lastIndexOf('```')
                                  jsonString = jsonString.substring(startIndex, endIndex).trim()
                                }
                                const feedbackData = JSON.parse(jsonString)
                                return (
                                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                                    {/* IELTS Criteria Scores */}
                                    <Grid container spacing={2}>
                                      {['Task Response', 'Coherence and Cohesion', 'Lexical Resource', 'Grammatical Range and Accuracy'].map((criteria) => {
                                        if (feedbackData[criteria]) {
                                          console.log({feedbackData})
                                          return (
                                            <Grid item xs={12} sm={6} key={criteria}>
                                              <Card variant="outlined" sx={{ p: 2, textAlign: 'center' }}>
                                                <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                                                  {criteria} 
                                                </Typography>
                                                <Chip 
                                                  label={`${feedbackData[criteria].score || 0}/9`} 
                                                  color={feedbackData[criteria] >= 7 ? 'success' : feedbackData[criteria] >= 5 ? 'warning' : 'error'}
                                                  size="large"
                                                />
                                              </Card>
                                            </Grid>
                                          )
                                        }
                                        return null
                                      })}
                                    </Grid>
                                    {['Task Response', 'Coherence and Cohesion', 'Lexical Resource', 'Grammatical Range and Accuracy'].map((criteria) => {
                                        if (feedbackData[criteria]) {
                                          console.log({feedbackData})
                                          return (
                                            <Card variant="outlined" sx={{ p: 2 }}>
                                        <Typography variant="h6" sx={{ mb: 1, color: 'primary.main' }}>
                                          📝 {criteria}
                                        </Typography>
                                        <Typography variant="body1">
                                          {feedbackData[criteria].comment}
                                        </Typography>
                                      </Card>
                                          )
                                        }
                                        return null
                                      })}
                                    {/* Structure Analysis */}
                                    {feedbackData.structure_analysis && (
                                      <Card variant="outlined" sx={{ p: 2 }}>
                                        <Typography variant="h6" sx={{ mb: 1, color: 'primary.main' }}>
                                          📝 Phân tích cấu trúc
                                        </Typography>
                                        <Typography variant="body1">
                                          {feedbackData.structure_analysis}
                                        </Typography>
                                      </Card>
                                    )}
                                    
                                    {/* Argument Strength */}
                                    {feedbackData.argument_strength && (
                                      <Card variant="outlined" sx={{ p: 2 }}>
                                        <Typography variant="h6" sx={{ mb: 1, color: 'secondary.main' }}>
                                          💪 Sức mạnh lập luận
                                        </Typography>
                                        <Typography variant="body1">
                                          {feedbackData.argument_strength}
                                        </Typography>
                                      </Card>
                                    )}
                                    
                                    {/* Overall Comments */}
                                    {feedbackData.overall_comment && (
                                      <Card variant="outlined" sx={{ p: 2 }}>
                                        <Typography variant="h6" sx={{ mb: 1, color: 'info.main' }}>
                                          💬 Nhận xét tổng thể
                                        </Typography>
                                        <Typography variant="body1">
                                          {feedbackData.overall_comment}
                                        </Typography>
                                      </Card>
                                    )}
                                  </Box>
                                )
                              } catch (error) {
                                // Fallback to ReactMarkdown if JSON parsing fails
                                return <div>{feedback.detailed_feedback}</div>
                              }
                            })()}
                          </Box>
                        )}
                        
                        {/* Fallback for other response formats */}
                        {!feedback.detailed_feedback && (feedback.feedback || feedback.response) && (
                          <ReactMarkdown>{feedback.feedback || feedback.response}</ReactMarkdown>
                        )}
                      </Paper>
                    </motion.div>
                  )}
                </>
              ) : (
                <Box sx={{ textAlign: 'center', py: 8 }}>
                  <EditIcon sx={{ fontSize: 80, color: 'grey.300', mb: 2 }} />
                  <Typography variant="h6" color="text.secondary">
                    Chọn một đề bài để bắt đầu viết
                  </Typography>
                </Box>
              )}
            </Paper>
          </Grid>
        </Grid>
      </motion.div>
    </Container>
  )
}

export default Writing